export const CAPTURE_VERSION = "proposal-partial/v1";
export const CAPTURE_INTERVAL_MS = 1500;
export const MAX_CAPTURE_BYTES = 48_000;

// Form identity selects the Sheet tab, regardless of the embedding page.
// Keep this map identical to TARGETS in the standalone Apps Script.
export const PROPOSAL_TARGETS = {
  staging: {
    formId: "31eeebde-3171-480b-a33b-b47dfe9486b8",
  },
  production: {
    formId: "ea4b6210-4488-4838-a38a-fa84b15bd53e",
  },
} as const;

// Explicitly reviewed form fields, including conditional property sections.
// Keep this list identical to ALLOWED_FIELDS in the standalone Apps Script.
export const PROPOSAL_FIELDS = [
  "email", "firstname", "lastname", "phone", "number_of_properties_forms",
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "purchase_vs_build", "purchase_vs_build_2", "purchase_vs_build_3",
  "property_address_1", "property_address_2", "property_address_3",
  "property_city_address_1", "property_city_address_2", "property_city_address_3",
  "property_state_address_1", "property_state_address_2", "property_state_address_3",
  "zip_code_1", "zip_code", "zip_code_3",
  "property_type_1", "property_type_2", "property_type_3",
  "property_purchase_price_1", "property_purchase_price_2", "property_purchase_price_3",
  "property_construction_costs_1", "property_construction_costs_2", "property_construction_costs_3",
  "n1031_basis_1", "n1031_basis_2", "n1031_basis_3",
  "purchase_date_or_pis_1", "purchase_date_or_pis_2", "purchase_date_or_pis_3",
  "property_renovation_costs_1", "property_renovation_costs_2", "property_renovation_costs_3",
  "renovation_scope_1", "renovation_scope_2", "renovation_scope_3",
  "n1031_exchange_1", "n1031_exchange_2", "n1031_exchange_3",
  "tax_year", "tax_year_2", "tax_year_3", "property_additional_information",
] as const;

const allowedFields = new Set<string>(PROPOSAL_FIELDS);
export type CapturedFields = Record<string, string | string[]>;
export type CaptureStatus = "partial" | "completed";
export type CaptureEnvironment = keyof typeof PROPOSAL_TARGETS;
type CaptureTarget = {
  [Environment in CaptureEnvironment]: {
    environment: Environment;
    formId: typeof PROPOSAL_TARGETS[Environment]["formId"];
  };
}[CaptureEnvironment];

export type CaptureConfig = CaptureTarget & {
  endpoint: string;
  pageUrl: string;
  pagePath: string;
};

type CapturePayloadBase = {
  session_id: string;
  snapshot_revision: number;
  status: CaptureStatus;
  capture_version: typeof CAPTURE_VERSION;
  instance_id: string;
  page_path: string;
  page_url: string;
  conversion_id: string;
  fields: CapturedFields;
};
export type CapturePayload = {
  [Environment in CaptureEnvironment]: {
    environment: Environment;
    form_id: typeof PROPOSAL_TARGETS[Environment]["formId"];
  } & CapturePayloadBase;
}[CaptureEnvironment];

export interface CaptureSnapshot {
  fields: CapturedFields;
  sessionId: string;
  instanceId: string;
  status: CaptureStatus;
  revision: number;
  conversionId?: string;
}

export function getCaptureConfig(
  environment: string | undefined,
  endpoint: string | undefined,
  pageUrl: string,
): CaptureConfig | null {
  try {
    if (environment !== "staging" && environment !== "production") return null;
    const page = new URL(pageUrl);
    if (!["http:", "https:"].includes(page.protocol) || page.username || page.password) return null;
    // Never send arbitrary query-string or fragment data to the Sheet.
    const metadata = { pageUrl: `${page.origin}${page.pathname}`, pagePath: page.pathname };
    if (metadata.pageUrl.length > 2048) return null;
    if (!endpoint || !/^https:\/\/script\.google\.com\/macros\/s\/[A-Za-z0-9_-]+\/exec$/.test(endpoint)) return null;
    return environment === "staging"
      ? { endpoint, environment, ...PROPOSAL_TARGETS.staging, ...metadata }
      : { endpoint, environment, ...PROPOSAL_TARGETS.production, ...metadata };
  } catch {
    return null;
  }
}

export function validEmail(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 254) return false;
  return /^[^\s@<>]+@[^\s@<>.]+(?:\.[^\s@<>.]+)+$/.test(value);
}

export function normalizeFields(raw: unknown): CapturedFields {
  if (!Array.isArray(raw)) return {};
  const entries: Array<[string, string | string[]]> = [];
  for (const field of raw.slice(0, 150)) {
    if (!field || typeof field !== "object" || typeof field.name !== "string") continue;
    const name = field.name.replace(/^0-1\//, "");
    if (!allowedFields.has(name)) continue;
    const limit = name === "property_additional_information" ? 8000 : 2000;
    let value: string | string[];
    if (typeof field.value === "string") {
      value = field.value.trim().slice(0, limit);
    } else if (typeof field.value === "boolean" || typeof field.value === "number") {
      value = String(field.value);
    } else if (Array.isArray(field.value) && field.value.every((item: unknown) => typeof item === "string")) {
      value = [...new Set<string>(field.value.map((item: string) => item.trim().slice(0, limit)))].sort().slice(0, 30);
    } else {
      continue;
    }
    if (name === "email" && typeof value === "string") value = value.toLowerCase();
    entries.push([name, value]);
  }
  return Object.fromEntries(entries.sort(([a], [b]) => a.localeCompare(b)));
}

export function fingerprint(fields: CapturedFields): string {
  return JSON.stringify(Object.fromEntries(
    Object.entries(fields)
      .filter(([, value]) => value !== "" && (!Array.isArray(value) || value.length > 0))
      .sort(([a], [b]) => a.localeCompare(b)),
  ));
}

export function eligiblePartial(fields: CapturedFields): boolean {
  return validEmail(fields.email);
}

export function makePayload(
  config: CaptureConfig,
  { fields, sessionId, instanceId, status, revision, conversionId = "" }: CaptureSnapshot,
): CapturePayload | null {
  if (!validEmail(fields.email) || !Number.isSafeInteger(revision) || revision < 1) return null;
  const base: CapturePayloadBase = {
    session_id: sessionId, snapshot_revision: revision, status, capture_version: CAPTURE_VERSION,
    instance_id: instanceId, page_path: config.pagePath, page_url: config.pageUrl,
    conversion_id: conversionId, fields,
  };
  return config.environment === "staging"
    ? {
      ...base, environment: "staging", form_id: PROPOSAL_TARGETS.staging.formId,
    }
    : {
      ...base, environment: "production", form_id: PROPOSAL_TARGETS.production.formId,
    };
}

export function serializePayload(payload: CapturePayload): string | null {
  const body = JSON.stringify(payload);
  return new TextEncoder().encode(body).byteLength <= MAX_CAPTURE_BYTES ? body : null;
}
